import type { Analytics, AnalyticsGlobalContext } from 'big-brother';
import type { Centrifuge as CentrifugeType } from 'centrifuge';
import { sentryCaptureError } from 'services/sentry/capture';
import { ANALYTICS_URL, getMappingChannel } from 'utils';
import { addPwPrefixToUrl } from 'utils/pwContext';

import { getClickStreamEvent } from './constants';
import type { AnalyticsEventPayload, ClickstreamProps, SelfDescribingJson } from './types';

export interface ClickstreamInstance {
  isFailed: () => boolean | null;
  isLoading: () => boolean | null;
  isInitialized: () => boolean | null;
  initClickstream: (data: ClickstreamProps) => Promise<void>;
  push: (event: AnalyticsEventPayload, context: Array<SelfDescribingJson>) => void;
  pageView: () => void;
  updateContext: (data: AnalyticsGlobalContext) => void;
}

let loading: boolean | null = null;
let loaded: boolean | null = null;
let failed: boolean | null = null;
let instance: Analytics | null = null;
let initAttempts = 0;

const MAX_CHUNK_RETRIES = 3;
const MAX_INIT_RETRIES = 2;
const CHUNK_RETRY_DELAY = 2000;
const INIT_RETRY_DELAY = 1000;

const isLoading = () => loading;
const isFailed = () => failed;
const isInitialized = () => loaded && loaded !== null;

const loadAnalyticsDeps = async (
  attempt = 0,
): Promise<[{ Centrifuge: typeof CentrifugeType }, { Analytics: typeof Analytics }]> => {
  try {
    const result = await Promise.all([
      import(/* webpackChunkName: "analytics", webpackPrefetch: true */ 'centrifuge'),
      import(/* webpackChunkName: "analytics", webpackPrefetch: true */ 'big-brother'),
    ]);
    return result as [{ Centrifuge: typeof CentrifugeType }, { Analytics: typeof Analytics }];
  } catch (e) {
    if (attempt < MAX_CHUNK_RETRIES) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, CHUNK_RETRY_DELAY * (attempt + 1));
      });
      return loadAnalyticsDeps(attempt + 1);
    }
    throw e;
  }
};

const initClickstream = async (props: ClickstreamProps) => {
  const baseConnectionUrl = `wss://${window.location.host}${ANALYTICS_URL}`;
  const connectionString = addPwPrefixToUrl(baseConnectionUrl);

  try {
    loading = true;
    initAttempts += 1;
    const [{ Centrifuge }, { Analytics }] = await loadAnalyticsDeps();
    const myCentrifugoInstance: CentrifugeType = new Centrifuge(connectionString, {
      data: {
        Authorization: props.authToken,
        'X-Api-Key': props.merchantApiKey,
      },
    });
    myCentrifugoInstance.connect();
    instance = new Analytics({
      appId: 'cashier',
      centrifugo: myCentrifugoInstance,
      crossdomainId: props.clickstreamId,
      globalContext: {
        channel: getMappingChannel(props.appChannel, props.platform),
        brand: props.brand,
        language: props.language,
      },
    });
    const context = props.cashierContext ? [props.cashierContext] : [];
    instance.push(getClickStreamEvent.pw_session_initiated, context);
    loaded = true;
    loading = false;
    initAttempts = 0;
  } catch (e) {
    sentryCaptureError(`Centrifuge error: ${JSON.stringify(e)}`, {
      brand: props.brand,
    });
    failed = true;
    loading = false;
    if (initAttempts < MAX_INIT_RETRIES) {
      setTimeout(() => {
        failed = null;
        loading = null;
        initClickstream(props);
      }, INIT_RETRY_DELAY * initAttempts);
    }
  }
};

const push = (event: AnalyticsEventPayload, context: Array<SelfDescribingJson> = []) => {
  if (!instance) return;
  instance.push(
    {
      schema: event.schema,
      data: event.data,
    },
    context,
  );
};

const pageView = () => {
  if (!instance) return;
  instance.pageview();
};

const updateContext = (data: AnalyticsGlobalContext) => {
  if (!instance) return;
  instance.updateDefaultGlobalContext(data);
};

export const getEventAnalytic = () => ({
  isFailed,
  isLoading,
  isInitialized,
  initClickstream,
  push,
  pageView,
  updateContext,
});
